<script lang="ts">
    import { useForm } from '@inertiajs/svelte';
    import { Button } from '@/components/ui/button';
    import { Input } from '@/components/ui/input';
    import Textarea from '@/components/Inputs/Textarea.svelte';
    import Checkbox from '@/components/Inputs/Checkbox.svelte';
    import Select from '@/components/Inputs/Select.svelte';
    import * as Card from '@/components/ui/card';
    import { Label } from '@/components/ui/label';

    type Props = {
        job: {
            id: string;
            title: string;
            address: string;
            postcode: string;
        };
    };

    let { job }: Props = $props();

    let form = $state(useForm({
        overall_satisfaction: '',
        quality_of_work_rating: null,
        professionalism_rating: null,
        timeliness_rating: null,
        cleanliness_rating: null,
        communication_rating: null,
        what_did_we_do_well: '',
        what_could_we_improve: '',
        general_comments: '',
        work_completed_as_expected: true,
        work_quality_comments: '',
        staff_courteous_and_professional: true,
        staff_feedback: '',
        site_left_clean_and_tidy: true,
        noise_levels_acceptable: true,
        disruption_minimized: true,
        site_management_comments: '',
        felt_safe_during_works: true,
        property_secure_at_all_times: true,
        safety_concerns: '',
        would_recommend_to_others: null,
        would_use_again: null,
        any_issues_to_address: false,
        issues_description: '',
        feedback_provided_by: '',
        consent_to_use_as_testimonial: false,
    }));

    function submit(e: Event) {
        e.preventDefault();
        $form.post(route('feedback.store', { job: job.id }));
    }

    const ratingOptions = [
        { label: '1 - Poor', value: 1 },
        { label: '2 - Fair', value: 2 },
        { label: '3 - Good', value: 3 },
        { label: '4 - Very Good', value: 4 },
        { label: '5 - Excellent', value: 5 },
    ];

    const satisfactionOptions = [
        { label: 'Very Satisfied', value: 'very_satisfied' },
        { label: 'Satisfied', value: 'satisfied' },
        { label: 'Neutral', value: 'neutral' },
        { label: 'Dissatisfied', value: 'dissatisfied' },
        { label: 'Very Dissatisfied', value: 'very_dissatisfied' },
    ];
</script>

<svelte:head>
    <title>Customer Feedback - {job.title}</title>
</svelte:head>

<div class="min-h-screen bg-background py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-7xl mx-auto">
        <div class="text-center mb-8">
            <h1 class="text-3xl font-bold">Customer Feedback Form</h1>
            <p class="text-muted-foreground mt-2">{job.title}</p>
            <p class="text-sm text-muted-foreground">{job.address.address_line_one}, {job.address.postcode}</p>
        </div>

        <form onsubmit={submit} class="grid grid-cols-1 lg:grid-cols-2 lg:gap-x-6">
            <!-- Overall Satisfaction -->
            <Card.Root class="mb-4">
                <Card.Header>
                    <Card.Title>Overall Satisfaction</Card.Title>
                </Card.Header>
                <Card.Content class="space-y-4">
                    <Select
                        label="How satisfied are you with our service?"
                        bind:value={$form.overall_satisfaction}
                        options={satisfactionOptions}
                        error={$form.errors.overall_satisfaction}
                    />
                </Card.Content>
            </Card.Root>

            <!-- Quality Ratings -->
            <Card.Root class="mb-4">
                <Card.Header>
                    <Card.Title>Service Quality</Card.Title>
                    <p class="text-sm text-muted-foreground">Please rate the following aspects (1-5)</p>
                </Card.Header>
                <Card.Content class="space-y-4">
                    <Select
                        label="Quality of Work"
                        bind:value={$form.quality_of_work_rating}
                        options={ratingOptions}
                        error={$form.errors.quality_of_work_rating}
                    />
                    <Select
                        label="Professionalism"
                        bind:value={$form.professionalism_rating}
                        options={ratingOptions}
                        error={$form.errors.professionalism_rating}
                    />
                    <Select
                        label="Timeliness"
                        bind:value={$form.timeliness_rating}
                        options={ratingOptions}
                        error={$form.errors.timeliness_rating}
                    />
                    <Select
                        label="Cleanliness"
                        bind:value={$form.cleanliness_rating}
                        options={ratingOptions}
                        error={$form.errors.cleanliness_rating}
                    />
                    <Select
                        label="Communication"
                        bind:value={$form.communication_rating}
                        options={ratingOptions}
                        error={$form.errors.communication_rating}
                    />
                </Card.Content>
            </Card.Root>

            <!-- Open Feedback -->
            <Card.Root class="mb-4">
                <Card.Header>
                    <Card.Title>Your Feedback</Card.Title>
                </Card.Header>
                <Card.Content class="space-y-4">
                    <Textarea
                        label="What did we do well?"
                        bind:value={$form.what_did_we_do_well}
                        error={$form.errors.what_did_we_do_well}
                    />
                    <Textarea
                        label="What could we improve?"
                        bind:value={$form.what_could_we_improve}
                        error={$form.errors.what_could_we_improve}
                    />
                    <Textarea
                        label="General Comments"
                        bind:value={$form.general_comments}
                        error={$form.errors.general_comments}
                    />
                </Card.Content>
            </Card.Root>

            <!-- Work Quality -->
            <Card.Root class="mb-4">
                <Card.Header>
                    <Card.Title>Work Completion</Card.Title>
                </Card.Header>
                <Card.Content class="space-y-4">
                    <Checkbox
                        bind:checked={$form.work_completed_as_expected}
                        label="Work was completed as expected"
                    />
                    <Textarea
                        label="Work Quality Comments"
                        bind:value={$form.work_quality_comments}
                        error={$form.errors.work_quality_comments}
                    />
                </Card.Content>
            </Card.Root>

            <!-- Staff Behavior -->
            <Card.Root class="mb-4">
                <Card.Header>
                    <Card.Title>Staff</Card.Title>
                </Card.Header>
                <Card.Content class="space-y-4">
                    <Checkbox
                        bind:checked={$form.staff_courteous_and_professional}
                        label="Staff were courteous and professional"
                    />
                    <Textarea
                        label="Staff Feedback"
                        bind:value={$form.staff_feedback}
                        error={$form.errors.staff_feedback}
                    />
                </Card.Content>
            </Card.Root>

            <!-- Site Management -->
            <Card.Root class="mb-4">
                <Card.Header>
                    <Card.Title>Site Management</Card.Title>
                </Card.Header>
                <Card.Content class="space-y-4">
                    <Checkbox
                        bind:checked={$form.site_left_clean_and_tidy}
                        label="Site was left clean and tidy"
                    />
                    <Checkbox
                        bind:checked={$form.noise_levels_acceptable}
                        label="Noise levels were acceptable"
                    />
                    <Checkbox
                        bind:checked={$form.disruption_minimized}
                        label="Disruption was minimized"
                    />
                    <Textarea
                        label="Site Management Comments"
                        bind:value={$form.site_management_comments}
                        error={$form.errors.site_management_comments}
                    />
                </Card.Content>
            </Card.Root>

            <!-- Safety & Security -->
            <Card.Root class="mb-4">
                <Card.Header>
                    <Card.Title>Safety & Security</Card.Title>
                </Card.Header>
                <Card.Content class="space-y-4">
                    <Checkbox
                        bind:checked={$form.felt_safe_during_works}
                        label="I felt safe during the works"
                    />
                    <Checkbox
                        bind:checked={$form.property_secure_at_all_times}
                        label="My property was secure at all times"
                    />
                    <Textarea
                        label="Safety Concerns (if any)"
                        bind:value={$form.safety_concerns}
                        error={$form.errors.safety_concerns}
                    />
                </Card.Content>
            </Card.Root>

            <!-- Recommendation -->
            <Card.Root class="mb-6">
                <Card.Header>
                    <Card.Title>Recommendation</Card.Title>
                </Card.Header>
                <Card.Content class="space-y-4">
                    <Checkbox
                        bind:checked={$form.would_recommend_to_others}
                        label="I would recommend this service to others"
                    />
                    <Checkbox
                        bind:checked={$form.would_use_again}
                        label="I would use this service again"
                    />
                </Card.Content>
            </Card.Root>

            <!-- Issues -->
            <Card.Root class="mb-6">
                <Card.Header>
                    <Card.Title>Issues</Card.Title>
                </Card.Header>
                <Card.Content class="space-y-4">
                    <Checkbox
                        bind:checked={$form.any_issues_to_address}
                        label="There are issues that need to be addressed"
                    />
                    {#if $form.any_issues_to_address}
                        <Textarea
                            label="Please describe the issues"
                            bind:value={$form.issues_description}
                            error={$form.errors.issues_description}
                        />
                    {/if}
                </Card.Content>
            </Card.Root>

            <!-- Contact Info -->
            <Card.Root class="mb-6">
                <Card.Header>
                    <Card.Title>Your Details</Card.Title>
                </Card.Header>
                <Card.Content class="space-y-4">
                    <div class="space-y-2">
                        <Label for="feedback_provided_by">Your Name (optional)</Label>
                        <Input
                            id="feedback_provided_by"
                            type="text"
                            bind:value={$form.feedback_provided_by}
                        />
                    </div>
                    <Checkbox
                        bind:checked={$form.consent_to_use_as_testimonial}
                        label="I consent to this feedback being used as a testimonial"
                    />
                </Card.Content>
            </Card.Root>
            <div>

            </div>

            <!-- Submit -->
            <div class="flex justify-end">
                <Button type="submit" size="lg" disabled={$form.processing}>
                    {#if $form.processing}
                        Submitting...
                    {:else}
                        Submit Feedback
                    {/if}
                </Button>
            </div>
        </form>
    </div>
</div>
